import { useAppForm } from "@shared/lib/form";
import { supabase } from "@shared/lib/supabase/client";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shared/ui/card";
import { KeyRound, Loader2 } from "lucide-react";
import { useIntl } from "react-intl";
import { toast } from "sonner";
import { z } from "zod";

const createSchema = (intl: ReturnType<typeof useIntl>) =>
	z
		.object({
			password: z.string().min(8, {
				message: intl.formatMessage({
					id: "validation.password.min",
					defaultMessage: "Minimum 8 characters",
				}),
			}),
			confirm: z.string(),
		})
		.refine((data) => data.password === data.confirm, {
			message: intl.formatMessage({
				id: "validation.password.match",
				defaultMessage: "Passwords do not match",
			}),
			path: ["confirm"],
		});

export function ChangePasswordCard() {
	const intl = useIntl();

	const form = useAppForm({
		defaultValues: {
			password: "",
			confirm: "",
		},
		validators: {
			onSubmit: createSchema(intl),
		},
		onSubmit: async ({ value }) => {
			const { error } = await supabase.auth.updateUser({
				password: value.password,
			});

			if (error) {
				toast.error(error.message);
			} else {
				toast.success(
					intl.formatMessage({
						id: "api.password.update.success",
						defaultMessage: "Password updated successfully",
					})
				);
				form.reset();
			}
		},
	});

	return (
		<div className="grid grid-cols-4 gap-4">
			<Card className=""></Card>
			<Card className="col-span-2">
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-base">
						<KeyRound className="h-4 w-4 text-muted-foreground" />
						{intl.formatMessage({
							id: "page.profile.security.changePassword",
							defaultMessage: "Change password",
						})}
					</CardTitle>
					<CardDescription>
						{intl.formatMessage({
							id: "page.profile.security.changePasswordDescription",
							defaultMessage: "Update your account password",
						})}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form.AppForm>
						<form
							onSubmit={(e) => {
								e.preventDefault();
								form.handleSubmit();
							}}
							className="space-y-4 max-w-sm"
						>
							<form.AppField name="password">
								{(field) => (
									<field.PasswordField
										label={intl.formatMessage({
											id: "page.profile.security.newPassword",
											defaultMessage: "New password",
										})}
									/>
								)}
							</form.AppField>

							<form.AppField name="confirm">
								{(field) => (
									<field.PasswordField
										label={intl.formatMessage({
											id: "page.profile.security.confirmPassword",
											defaultMessage: "Confirm password",
										})}
									/>
								)}
							</form.AppField>

							<div className="flex justify-end pt-2">
								<form.Subscribe selector={(state) => state.isSubmitting}>
									{(isSubmitting) => (
										<Button type="submit" disabled={isSubmitting} className="min-w-32">
											{isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
											{intl.formatMessage({
												id: "page.profile.security.updatePassword",
												defaultMessage: "Update password",
											})}
										</Button>
									)}
								</form.Subscribe>
							</div>
						</form>
					</form.AppForm>
				</CardContent>
			</Card>
			<Card className=""></Card>
		</div>
	);
}
